import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Mail, Phone, MapPin, Globe, ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { HasMark, CompanyName } from "@/components/BrandText";
import { COMPANY, SERVICES, IMAGES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "", service: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setSubmitted(true);
      toast.success("Thank you. We will reach out shortly.");
      setForm({ full_name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.message || "Submission failed";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="pt-32">
      <Toaster theme="dark" position="top-center" richColors />

      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-16 md:pt-20 md:pb-20">
          <Reveal>
            <div className="overline mb-5">Contact <HasMark /></div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-4xl md:text-7xl text-white leading-[1.05] tracking-tight max-w-4xl">
              Book Your <span className="italic gold-gradient-text">Consultation Today</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 max-w-3xl text-white/70 text-lg leading-relaxed">
              Share a few details about your business and our advisory team will respond personally within one
              business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
          {/* Left info card */}
          <Reveal className="lg:col-span-5">
            <div className="relative h-full lux-card gold-brackets overflow-hidden">
              <img
                src={IMAGES.boardroom}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-25"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/95 via-[#0A0A0A]/80 to-[#0B3D2E]/60" />

              <div className="relative p-8 md:p-10 flex flex-col h-full justify-between min-h-[520px]">
                <div>
                  <img src={COMPANY.logo} alt="HAS UMC" className="h-24 w-24 object-contain bg-black ring-1 ring-[#D4AF37]/40 mb-6" />
                  <h3 className="font-display text-3xl text-white leading-tight"><CompanyName /></h3>
                  <p className="mt-3 text-white/65 leading-relaxed text-sm">
                    Strategic Advisory · Market Intelligence · Business Growth
                  </p>
                  <div className="diamond-divider my-8" />
                  <ul className="space-y-5">
                    <li className="flex items-start gap-4">
                      <div className="h-10 w-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] shrink-0"><MapPin size={16} /></div>
                      <div>
                        <div className="overline text-[10px]">Office</div>
                        <div className="text-white/85 text-sm mt-1" data-testid="contact-location">{COMPANY.location}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="h-10 w-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] shrink-0"><Phone size={16} /></div>
                      <div>
                        <div className="overline text-[10px]">Phone</div>
                        <a href={`tel:${COMPANY.phone.replace(/\s/g,'')}`} className="text-white/85 text-sm mt-1 hover:text-[#D4AF37]" data-testid="contact-phone">{COMPANY.phone}</a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="h-10 w-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] shrink-0"><Mail size={16} /></div>
                      <div>
                        <div className="overline text-[10px]">Email</div>
                        <a href={`mailto:${COMPANY.email}`} className="text-white/85 text-sm mt-1 hover:text-[#D4AF37]" data-testid="contact-email">{COMPANY.email}</a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="h-10 w-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] shrink-0"><Globe size={16} /></div>
                      <div>
                        <div className="overline text-[10px]">Website</div>
                        <div className="text-white/85 text-sm mt-1">{COMPANY.website}</div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="mt-10 pt-6 border-t border-white/10">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">{COMPANY.trust}</p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right form */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="lux-card p-7 md:p-10">
              {submitted ? (
                <div data-testid="contact-success" className="py-12 text-center">
                  <CheckCircle2 size={48} className="text-[#D4AF37] mx-auto" />
                  <h3 className="font-display text-3xl text-white mt-6">Thank You.</h3>
                  <p className="mt-3 text-white/70 max-w-md mx-auto">
                    Your consultation request has been received. A member of our advisory team will contact you within one business day.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-submit-another"
                    className="btn-ghost mt-8"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="consultation-form" className="space-y-5">
                  <div className="overline mb-2">Consultation Request</div>
                  <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                    Tell us about your business
                  </h3>

                  <div className="grid sm:grid-cols-2 gap-5 pt-3">
                    <Field
                      label="Full Name *"
                      testid="contact-full-name"
                      value={form.full_name}
                      onChange={update("full_name")}
                      required
                    />
                    <Field
                      label="Email *"
                      type="email"
                      testid="contact-email-input"
                      value={form.email}
                      onChange={update("email")}
                      required
                    />
                    <Field
                      label="Phone *"
                      testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      required
                    />
                    <Field
                      label="Company"
                      testid="contact-company"
                      value={form.company}
                      onChange={update("company")}
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">
                      Service of Interest *
                    </label>
                    <select
                      value={form.service}
                      onChange={update("service")}
                      data-testid="contact-service"
                      required
                      className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#D4AF37] outline-none text-white px-4 py-3 text-sm transition-colors"
                    >
                      <option value="">Select a practice…</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title}>{s.title}</option>
                      ))}
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">
                      How can we help?
                    </label>
                    <textarea
                      value={form.message}
                      onChange={update("message")}
                      data-testid="contact-message"
                      rows={5}
                      placeholder="Briefly describe your business challenge or growth goal…"
                      className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#D4AF37] outline-none text-white px-4 py-3 text-sm transition-colors resize-y"
                    />
                  </div>

                  <div className="pt-2 flex flex-wrap items-center gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit"
                      className="btn-gold"
                    >
                      {loading ? "Submitting…" : "Submit Request"}
                      {!loading && <ArrowRight size={18} />}
                    </button>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/45">
                      Confidential · Senior-led response
                    </p>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#D4AF37] outline-none text-white px-4 py-3 text-sm transition-colors"
      />
    </div>
  );
}
